import './user-avatar.css';

import {
  Avatar,
  AvatarTypeMap,
  Button,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import React, { useState } from 'react';
import { isFunction, omit } from 'lodash';

import AssetUploadDialog from '../../shared/components/AssetUploadDialog';
import AuthService from '../../shared/services/AuthService';
import { DefaultComponentProps } from '@mui/material/OverridableComponent';
import EditIcon from '@mui/icons-material/Edit';
import Grid from '@mui/material/Unstable_Grid2';
import { styled } from '@mui/system';
import { useAvatarContext } from '../../membership/hooks/AvatarContext';
import useAvatars from '../../membership/hooks/useAvatars';

export interface IUserAvatarProps extends DefaultComponentProps<AvatarTypeMap> {
  /** Identifier of user account. */
  userkey: number;
  /**
   * Value indicating whether an image is required, rendering a red border if
   * an avatar image isn't specified.
   */
  required?: boolean;
  /** Optional value indicating whether the avatar can be edited here. */
  edit?: boolean;
  /** Optional handler invoked when the avatar is changed. */
  onAvatarChange?: (value: string) => void;
}

const UserAvatar = (props: IUserAvatarProps) => {
  return props.userkey === AuthService.getUserkey() ? (
    <MyUserAvatar {...props} />
  ) : (
    <AnyUserAvatar {...props} />
  );
};

/** Avatar for current user. */
const MyUserAvatar = (props: IUserAvatarProps) => {
  const avatarContext = useAvatarContext();
  return <UserAvatarComponent {...props} {...avatarContext} />;
};

/** Avatar for users other than the current user. */
const AnyUserAvatar = (props: IUserAvatarProps) => {
  const avatarContext = useAvatars(props.userkey);
  return <UserAvatarComponent {...props} {...avatarContext} />;
};

interface IUserAvatarComponentProps extends IUserAvatarProps {
  /** User's friendly name to show as image alt text. */
  friendlyname?: string;
  /** User's initials to show if no image. */
  initials?: string;
  /** Full image URL including sas. */
  src?: string;
  /** Optional value indicating the avatar is being loaded or uploaded. */
  uploading?: boolean;
  /** Optional callback to se the source when editing the avatar. */
  setSrc?: (value: string) => void;
  /** Optional handler to upload the current user's avatar. */
  upload?: (file: File) => Promise<string>;
  /** Optional handler to remove the current user's avatar. */
  remove?: () => Promise<void>;
}

/**
 * Avatar that uses the profile image from the membership service.
 */
const UserAvatarComponent = (props: IUserAvatarComponentProps) => {
  const {
    friendlyname,
    initials,
    src,
    uploading,
    edit,
    required,
    upload,
    remove
  } = props;

  // Change avatar background color when there is 'No image chosen'
  const CustomAvatar = styled(Avatar)({
    backgroundColor: edit ? '#F2F2F2 !important' : '',
    '&.MuiAvatar-root': {
      backgroundColor: edit ? '#F2F2F2 !important' : ''
    }
  });

  /** Value indicating whether to show the upload dialog. */
  const [showUpload, setShowUpload] = useState(false);
  /** Handle the upload dialog Cancel by hiding the dialog. */
  const onUpload = async (files: File[]) => {
    setShowUpload(false);
    if (files?.length > 0) {
      const assetpath = await upload(files[0]);
      props.onAvatarChange && props.onAvatarChange(assetpath);
    }
  };

  /** Remove our props from those passed to `Avatar` component. */
  const avatarProps = omit(props, [
    'edit',
    'friendlyname',
    'initials',
    'onAvatarChange',
    'remove',
    'required',
    'src',
    'setSrc',
    'upload',
    'uploading',
    'userkey'
  ]);

  /** Profile image upload is only available for the user themselves. */
  const enableUpload = isFunction(props.upload);

  return (
    <>
      <Grid
        container
        direction="column"
        alignItems="center"
        className="user-avatar"
      >
        <Grid className="avatar-container">
          <div className={required && !src ? 'missing-required' : ''}>
            <Tooltip
              title={friendlyname || initials || 'User'}
              enterDelay={800}
            >
              <CustomAvatar {...avatarProps} src={src} alt={friendlyname}>
                {edit ? (
                  <div className="noImage">
                    <div className="noImageText">No image chosen</div>
                  </div>
                ) : (
                  initials
                )}
              </CustomAvatar>
            </Tooltip>
          </div>
          {edit && src && enableUpload && (
            <IconButton
              className="edit-button"
              onClick={() => setShowUpload(true)}
              size="small"
              disabled={uploading}
            >
              <EditIcon />
            </IconButton>
          )}
          {uploading && (
            <CircularProgress size={24} className="upload-spinner" />
          )}
        </Grid>

        {edit && enableUpload && (
          <Grid>
            {src ? (
              <Button
                onClick={async () => {
                  await remove();
                  if (isFunction(props.onAvatarChange)) {
                    props.onAvatarChange(null);
                  }
                }}
                variant="text"
                size="small"
                disabled={uploading}
              >
                Remove image
              </Button>
            ) : (
              <Button
                onClick={() => setShowUpload(true)}
                variant="text"
                size="small"
                disabled={uploading}
              >
                Upload an image
              </Button>
            )}
          </Grid>
        )}
      </Grid>
      {showUpload && (
        <AssetUploadDialog
          title="Profile image"
          accept="image/*"
          multiple={false}
          onUpload={onUpload}
          onClose={() => setShowUpload(false)}
        />
      )}
    </>
  );
};

export default UserAvatar;
